import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Select } from '@/components/live';
import { callRpc } from '@/api/resources';
import { referenceValues } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';

const ADD = '__add__';

/**
 * Caste as a dropdown the field can add to (owner, 2026-09-21: "caste: dropdown with list + add
 * from dropdown"). The list is the organisation's `caste` reference set (migration 0062); the
 * last option adds a new one, which anyone who registers farmers may do. The farmer keeps the
 * caste as text, so a name already on a record still shows even if it is not on the list.
 */
export function CastePicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const castes = useQuery({ queryKey: ['ref', 'caste'], queryFn: () => referenceValues('caste'), enabled: isSupabaseConfigured });

  const labels = (castes.data ?? []).map((row) => row.label);
  if (value && !labels.some((label) => label.toLowerCase() === value.toLowerCase())) labels.unshift(value);
  const options = [
    { id: '', label: '--' },
    ...labels.map((label) => ({ id: label, label })),
    { id: ADD, label: '+ Add a caste...' },
  ];

  async function add() {
    const label = name.trim();
    if (!label) return;
    setSaving(true);
    try {
      await callRpc<string>('fn_add_reference_value', { p_set_key: 'caste', p_label: label });
      await queryClient.invalidateQueries({ queryKey: ['ref', 'caste'] });
      // An existing entry differing only in case is picked instead of duplicated.
      const existing = labels.find((entry) => entry.toLowerCase() === label.toLowerCase());
      onChange(existing ?? label);
      setAdding(false);
      setName('');
      toast.success(`Added "${label}".`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That caste could not be added.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Select onChange={(next) => (next === ADD ? setAdding(true) : onChange(next))} options={options}
              placeholder={castes.isLoading ? 'Loading...' : 'Select caste'} value={value} />
      {adding && (
        <div className="inline-add">
          <input autoFocus onChange={(event) => setName(event.target.value)}
                 onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void add(); } }}
                 placeholder="Caste name, as the farmer gives it" value={name} />
          <button className="primary" disabled={saving || !name.trim()} onClick={() => void add()} type="button">Add</button>
          <button aria-label="Cancel" onClick={() => { setAdding(false); setName(''); }} type="button"><X size={16} /></button>
        </div>
      )}
    </>
  );
}
